export function getEffectivePrice(product) {
  if (product.discountPrice != null && product.discountPrice < product.price) return product.discountPrice;
  return product.price;
}

export function isOnSale(product) {
  return product.discountPrice != null && product.discountPrice < product.price;
}

function getProductAttributeEntries(product) {
  const entries = [];
  (product.attributeValues || []).forEach((av) => {
    if (av.optionValue) entries.push(av);
  });
  (product.variants || []).forEach((variant) => {
    (variant.attributeValues || []).forEach((av) => {
      if (av.optionValue) entries.push(av);
    });
  });
  return entries;
}

export function computePriceBounds(products) {
  if (!products.length) return { min: 0, max: 0 };
  let min = Infinity;
  let max = 0;
  products.forEach((p) => {
    const price = getEffectivePrice(p);
    if (price < min) min = price;
    if (price > max) max = price;
  });
  return { min: Math.floor(min), max: Math.ceil(max) };
}

export function computeAttributeGroups(products) {
  const groups = new Map();

  products.forEach((product) => {
    const seenInProduct = new Set();
    getProductAttributeEntries(product).forEach((entry) => {
      const key = `${entry.attributeId}:${entry.optionValue}`;
      if (seenInProduct.has(key)) return;
      seenInProduct.add(key);

      if (!groups.has(entry.attributeId)) {
        groups.set(entry.attributeId, {
          attributeId: entry.attributeId,
          name: entry.attributeName,
          nameArabic: entry.attributeNameArabic,
          nameHebrew: entry.attributeNameHebrew,
          options: new Map(),
        });
      }

      const group = groups.get(entry.attributeId);
      if (!group.options.has(entry.optionValue)) {
        group.options.set(entry.optionValue, {
          value: entry.optionValue,
          valueArabic: entry.optionValueArabic,
          valueHebrew: entry.optionValueHebrew,
          count: 0,
        });
      }
      group.options.get(entry.optionValue).count += 1;
    });
  });

  return Array.from(groups.values())
    .map((g) => ({ ...g, options: Array.from(g.options.values()).sort((a, b) => b.count - a.count) }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

function matchesNonAttributeFilters(product, filters) {
  const price = getEffectivePrice(product);
  if (filters.minPrice !== "" && price < Number(filters.minPrice)) return false;
  if (filters.maxPrice !== "" && price > Number(filters.maxPrice)) return false;
  if (filters.onSaleOnly && !isOnSale(product)) return false;
  if (filters.inStockOnly && (product.stockQuantity ?? 0) <= 0) return false;
  if (filters.minRating > 0 && (product.rating ?? 0) < filters.minRating) return false;
  return true;
}

function matchesAttributeFilters(product, filters, excludeAttributeId) {
  const selectedEntries = Object.entries(filters.attributes || {}).filter(
    ([attrId, values]) => values.size > 0 && Number(attrId) !== excludeAttributeId,
  );
  if (selectedEntries.length === 0) return true;

  const productEntries = getProductAttributeEntries(product);
  return selectedEntries.every(([attrId, values]) =>
    productEntries.some((e) => String(e.attributeId) === attrId && values.has(e.optionValue)),
  );
}

export function filterProducts(products, filters, excludeAttributeId = null) {
  return products.filter(
    (p) => matchesNonAttributeFilters(p, filters) && matchesAttributeFilters(p, filters, excludeAttributeId),
  );
}

export function sortProducts(products, sortKey) {
  const sorted = [...products];
  switch (sortKey) {
    case "priceAsc":
      sorted.sort((a, b) => getEffectivePrice(a) - getEffectivePrice(b));
      break;
    case "priceDesc":
      sorted.sort((a, b) => getEffectivePrice(b) - getEffectivePrice(a));
      break;
    case "bestselling":
      sorted.sort((a, b) => (b.soldCount ?? 0) - (a.soldCount ?? 0));
      break;
    case "toprated":
      sorted.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
      break;
    default:
      sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }
  return sorted;
}

export function createEmptyFilters() {
  return {
    sort: "newest",
    onSaleOnly: false,
    inStockOnly: false,
    minPrice: "",
    maxPrice: "",
    minRating: 0,
    attributes: {},
  };
}

export function hasActiveFilters(filters) {
  return (
    filters.onSaleOnly ||
    filters.inStockOnly ||
    filters.minPrice !== "" ||
    filters.maxPrice !== "" ||
    filters.minRating > 0 ||
    Object.values(filters.attributes || {}).some((set) => set.size > 0)
  );
}
